import * as Y from 'yjs'
import { yXmlFragmentToProsemirrorJSON } from 'y-prosemirror'
import { strToU8, zipSync, type Zippable } from 'fflate'
import { useCorpusStore, type CorpusNode, type TrashEntry } from '../../stores/corpus'
import { useSettingsStore } from '../../stores/settings'
import { getPlatformBridge, isDesktopPlatform, isPlatformWeb } from '../platform/platformBridge'
import { keyManager } from '../crypto/keyManager'
import { syncEngine } from '../sync/syncEngine'
import { fetchPageNow, type MissingPageRead } from '../sync/pageCatchUp'
import { EXPORTING, pageReadMessage } from '../pages/pageReadMessages'
import { LocalYjsDocument, readLocalYjsState } from '../pages/yjsLocalPersistence'
import { readCoreFontBytes, readInstalledFamilies } from '../fonts/fontCatalogStorage'
import { getCoreFontFiles } from '../fonts/fontCatalog'
import { cleanMarkdownForExport, tryParseTiptapJson } from '../../utils/contentUtils'
import {
  SEI_FORMAT,
  SEI_FORMAT_VERSION,
  SEI_OMITTED_SETTINGS,
  SEI_PATHS,
  buildReadme,
  countCorpus,
  encryptArchive,
  flattenCorpusNodes,
  jsonBytes,
  packArchive,
  type SeiHistoryIndexEntry,
  type SeiInterfaceState,
  type SeiManifest,
  type SeiPage,
  type SeiSettingsFile,
  type SeiTree,
} from './seiFormat'

export interface SeiExportOptions {
  includeHistory: boolean
  includeFonts: boolean
  /** Null or empty for an unprotected file. */
  password: string | null
}

export interface TransferProgress {
  step: string
  current: number
  total: number
}

interface SeiExportResult {
  bytes: Uint8Array
  fileName: string
  /** Versions that were listed but whose content could not be fetched. */
  skippedSnapshots: number
}

type ProgressCallback = (progress: TransferProgress) => void

function appInfo(): SeiManifest['app'] {
  return {
    name: 'Superelectric Ink',
    version: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'unknown',
    platform: isPlatformWeb.value ? 'web' : isDesktopPlatform.value ? 'desktop' : 'mobile',
  }
}

/** Deep copy without pages that were never saved (they have no content). */
function cleanNodes(nodes: CorpusNode[]): CorpusNode[] {
  return nodes
    .filter((node) => !node.isUnsaved)
    .map((node) => {
      const { isUnsaved: _unsaved, children, ...rest } = JSON.parse(JSON.stringify(node)) as CorpusNode
      return { ...rest, children: cleanNodes(children || []) }
    })
}

function cleanTrash(entries: TrashEntry[]): TrashEntry[] {
  return entries
    .filter((entry) => !entry.node.isUnsaved)
    .map((entry) => ({ ...JSON.parse(JSON.stringify(entry)), node: cleanNodes([entry.node])[0] }))
}

/**
 * Reads a page the way the editor would show it: the local CRDT replica when
 * this device has one (it is never older than the flat file), otherwise the
 * flat page file, otherwise the page fetched from its edit log.
 */
async function loadPage(node: CorpusNode, inTrash: boolean, editor: any): Promise<SeiPage> {
  const corpus = useCorpusStore()
  const api = getPlatformBridge()

  if (!inTrash && node.id === corpus.activePageId && editor?.getJSON) {
    return { id: node.id, content: JSON.stringify(editor.getJSON(), null, 2) }
  }

  let hadLocalReplica = false
  const key = keyManager.getDocumentKey(node.id)
  if (key) {
    const ydoc = new Y.Doc()
    try {
      hadLocalReplica = await readLocalYjsState(node.id, ydoc, key)
      const fragment = ydoc.getXmlFragment('default')
      if (hadLocalReplica && fragment.length > 0) {
        return { id: node.id, content: JSON.stringify(yXmlFragmentToProsemirrorJSON(fragment), null, 2) }
      }
    } catch (error) {
      console.warn(`[SeiExport] Could not read the local replica of ${node.id}:`, error)
    } finally {
      ydoc.destroy()
    }
  }

  const result = inTrash ? await api.readTrashedPage(node.id) : await api.readPage(node.id)
  if (result?.success && result.content !== undefined) {
    return { id: node.id, content: result.content }
  }

  // A trashed page not stored on this device (a new device, or cleared
  // browser storage) is fetched from its edit log; readPage already does
  // this for the rest. It isn't saved, since its file belongs in the Trash.
  if (inTrash) {
    const content = await fetchPageNow(node.id, { saveFile: false }).catch(() => null)
    if (content !== null) return { id: node.id, content }
  }

  // A replica that exists but is empty is a genuinely empty page.
  if (hadLocalReplica) return { id: node.id, content: JSON.stringify({ type: 'doc', content: [] }) }
  const problem = (result as MissingPageRead | null)?.problem
  if (problem) throw new Error(pageReadMessage(problem, node.name, EXPORTING))
  throw new Error(`Could not read “${node.name}”. ${result?.error || ''}`.trim())
}

async function loadHistory(docId: string): Promise<{
  snapshots: Array<{ timestamp: number; wordCount: number; label: string; content: string }>
  skipped: number
}> {
  const api = getPlatformBridge()
  let versions: Array<{ id: string; timestamp: number; wordCount: number; label: string }> = []
  try {
    versions = syncEngine.canSync.value
      ? await syncEngine.listDocumentVersions(docId)
      : await api.listHistory(docId)
  } catch {
    versions = await api.listHistory(docId).catch(() => [])
  }

  const snapshots: Array<{ timestamp: number; wordCount: number; label: string; content: string }> = []
  let skipped = 0
  for (const version of versions) {
    const content = await syncEngine.fetchSnapshotContent(docId, version.id).catch(() => null)
    if (content === null || content === undefined) {
      skipped++
      continue
    }
    snapshots.push({ timestamp: version.timestamp, wordCount: version.wordCount, label: version.label, content })
  }
  snapshots.sort((a, b) => a.timestamp - b.timestamp)
  return { snapshots, skipped }
}

// ── Readable Markdown copies ─────────────────────────────────────────────────

const WINDOWS_RESERVED = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i

function safeName(name: string): string {
  const cleaned = name
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '-')
    .trim()
    .replace(/[. ]+$/g, '')
    .slice(0, 120)
  if (!cleaned || cleaned === '.' || cleaned === '..') return 'Untitled'
  return WINDOWS_RESERVED.test(cleaned.split('.')[0]) ? `_${cleaned}` : cleaned
}

function uniqueName(name: string, used: Set<string>): string {
  const base = safeName(name)
  let candidate = base
  let suffix = 2
  while (used.has(candidate.toLocaleLowerCase())) candidate = `${base} (${suffix++})`
  used.add(candidate.toLocaleLowerCase())
  return candidate
}

function toMarkdown(page: SeiPage, editor: any): string | null {
  const doc = tryParseTiptapJson(page.content)
  if (!doc || typeof editor?.markdown?.serialize !== 'function') return null
  try {
    return cleanMarkdownForExport(editor.markdown.serialize(doc))
  } catch {
    return null
  }
}

function addReadableTree(
  nodes: CorpusNode[],
  directory: string,
  pages: Map<string, SeiPage>,
  editor: any,
  files: Map<string, Uint8Array>,
): void {
  // Files and folders are named independently, so a page and the folder
  // holding its notes can share a stem ("Chapter.md", "Chapter/").
  const usedFiles = new Set<string>()
  const usedFolders = new Set<string>()
  for (const node of nodes) {
    if (node.subtype === 'page') {
      const stem = uniqueName(node.name, usedFiles)
      const page = pages.get(node.id)
      const markdown = page ? toMarkdown(page, editor) : null
      if (markdown !== null) files.set(`${directory}${stem}.md`, strToU8(markdown))
      if (node.children?.length) {
        const folder = uniqueName(stem, usedFolders)
        addReadableTree(node.children, `${directory}${folder}/`, pages, editor, files)
      }
    } else {
      const folder = uniqueName(node.name, usedFolders)
      addReadableTree(node.children || [], `${directory}${folder}/`, pages, editor, files)
    }
  }
}

// ── Export ───────────────────────────────────────────────────────────────────

function exportFileName(): string {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  return `Superelectric Ink Corpus ${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.sei`
}

/**
 * The saved corpus (tree and Trash) and every page's content, read once for an
 * export. With `skipUnreadable`, a page that can't be read is left out and
 * named in `unreadable` instead of failing the whole export.
 */
async function collectCorpus(editor: any, onProgress: ProgressCallback, skipUnreadable = false) {
  const corpus = useCorpusStore()
  const tree = cleanNodes(corpus.tree)
  const trash = cleanTrash(corpus.trash)
  const pageNodes = [
    ...flattenCorpusNodes(tree).map(({ node }) => ({ node, inTrash: false })),
    ...trash.flatMap((entry) => flattenCorpusNodes([entry.node]).map(({ node }) => ({ node, inTrash: true }))),
  ].filter(({ node }) => node.subtype === 'page')

  // Open pages write their local cache only periodically; bring every one up
  // to date so pages read back from storage include recent remote edits.
  await LocalYjsDocument.persistAll()

  const pages = new Map<string, SeiPage>()
  const unreadable: string[] = []
  for (let index = 0; index < pageNodes.length; index++) {
    const { node, inTrash } = pageNodes[index]
    onProgress({ step: 'Reading pages', current: index + 1, total: pageNodes.length })
    try {
      pages.set(node.id, await loadPage(node, inTrash, editor))
    } catch (error) {
      if (!skipUnreadable) throw error
      console.warn(`[SeiExport] Left out “${node.name}”:`, error)
      unreadable.push(node.name)
    }
  }
  return { tree, trash, pageNodes, pages, unreadable }
}

/** Builds a complete `.sei` archive of the current corpus and settings. */
async function buildCorpusArchive(
  editor: any,
  options: SeiExportOptions,
  onProgress: ProgressCallback = () => {},
): Promise<SeiExportResult> {
  const corpus = useCorpusStore()
  const settings = useSettingsStore()
  const files = new Map<string, Uint8Array>()

  const { tree, trash, pageNodes, pages } = await collectCorpus(editor, onProgress)
  const activeNode = corpus.activePageId ? corpus.findNode(corpus.activePageId) : null
  const treeFile: SeiTree = {
    version: 2,
    activePageId: activeNode && !activeNode.isUnsaved ? activeNode.id : null,
    tree,
    trash,
  }
  files.set(SEI_PATHS.tree, jsonBytes(treeFile))
  for (const [id, page] of pages) {
    files.set(`${SEI_PATHS.pages}${id}.json`, strToU8(page.content))
  }

  let snapshotCount = 0
  let skippedSnapshots = 0
  if (options.includeHistory) {
    for (let index = 0; index < pageNodes.length; index++) {
      const { node } = pageNodes[index]
      onProgress({ step: 'Reading version history', current: index + 1, total: pageNodes.length })
      const { snapshots, skipped } = await loadHistory(node.id)
      skippedSnapshots += skipped
      if (!snapshots.length) continue
      const indexEntries: SeiHistoryIndexEntry[] = snapshots.map((snapshot, position) => {
        const file = `${String(position + 1).padStart(4, '0')}.json`
        files.set(`${SEI_PATHS.history}${node.id}/${file}`, strToU8(snapshot.content))
        return { file, timestamp: snapshot.timestamp, wordCount: snapshot.wordCount, label: snapshot.label }
      })
      files.set(`${SEI_PATHS.history}${node.id}/index.json`, jsonBytes(indexEntries))
      snapshotCount += snapshots.length
    }
  }

  onProgress({ step: 'Collecting settings', current: 0, total: 0 })
  const values = settings.exportableSettings()
  for (const key of SEI_OMITTED_SETTINGS) delete values[key]
  const settingsFile: SeiSettingsFile = { values, omitted: [...SEI_OMITTED_SETTINGS] }
  files.set(SEI_PATHS.settings, jsonBytes(settingsFile))
  const { destination: _destination, ...localBackup } = JSON.parse(JSON.stringify(settings.backupSettings))
  files.set(SEI_PATHS.localBackup, jsonBytes(localBackup))
  const interfaceState: SeiInterfaceState = {
    focusStack: [...corpus.focusStack],
    pinned: corpus.pinned,
    searchView: corpus.searchView,
  }
  files.set(SEI_PATHS.interfaceState, jsonBytes(interfaceState))

  let fontCount = 0
  if (options.includeFonts && settings.installedFontIds.length) {
    onProgress({ step: 'Collecting fonts', current: 0, total: 0 })
    const families = await readInstalledFamilies(settings.installedFontIds)
    for (const family of families) {
      for (const file of getCoreFontFiles(family)) {
        const bytes = await readCoreFontBytes(file.path).catch(() => null)
        if (bytes) files.set(`${SEI_PATHS.fontFiles}${file.path}`, new Uint8Array(bytes))
      }
    }
    if (families.length) files.set(SEI_PATHS.fontFamilies, jsonBytes(families))
    fontCount = families.length
  }

  onProgress({ step: 'Writing readable copies', current: 0, total: 0 })
  files.set(SEI_PATHS.readme, strToU8(buildReadme(false)))
  addReadableTree(tree, SEI_PATHS.readable, pages, editor, files)
  if (trash.length) {
    addReadableTree(trash.map((entry) => entry.node), `${SEI_PATHS.readable}Trash/`, pages, editor, files)
  }

  onProgress({ step: 'Packing the file', current: 0, total: 0 })
  const app = appInfo()
  const inner = await packArchive(files, {
    format: SEI_FORMAT,
    formatVersion: SEI_FORMAT_VERSION,
    createdAt: new Date().toISOString(),
    app,
    contents: { ...countCorpus(tree, trash), snapshots: snapshotCount, fonts: fontCount },
    includes: { settings: true, history: options.includeHistory, fonts: fontCount > 0, readable: true },
  })

  let bytes = inner
  if (options.password) {
    onProgress({ step: 'Encrypting', current: 0, total: 0 })
    bytes = await encryptArchive(inner, options.password, app, buildReadme(true))
  }

  return { bytes, fileName: exportFileName(), skippedSnapshots }
}

// ── Markdown export ──────────────────────────────────────────────────────────

const MARKDOWN_README = [
  'Your writing from Superelectric Ink, as Markdown.',
  '',
  'Desks and folders are folders. Each page is a .md file, and a page\'s notes',
  'are in the folder with the same name beside it. Pages that were in the',
  'Trash are in the Trash folder.',
  '',
  'Markdown is plain text: any text editor opens it, and most writing apps',
  '(Word, Google Docs, Scrivener, Obsidian, Ulysses) can import it.',
  '',
].join('\n')

/**
 * Exports every page as Markdown in an ordinary .zip that mirrors the corpus,
 * readable without Superelectric Ink. Asks where to save; null when canceled.
 */
export async function exportCorpusAsMarkdown(
  editor: any,
  onProgress: ProgressCallback = () => {},
): Promise<{ path: string; unreadable: string[] } | null> {
  const { tree, trash, pages, unreadable } = await collectCorpus(editor, onProgress, true)

  onProgress({ step: 'Converting to Markdown', current: 0, total: 0 })
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  const name = `Superelectric Ink Writing ${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
  const root = `${name}/`
  const readme = unreadable.length
    ? `${MARKDOWN_README}\nThese pages could not be read and are not included:\n${unreadable.map((page) => `  - ${page}`).join('\n')}\n`
    : MARKDOWN_README
  const files = new Map<string, Uint8Array>([[`${root}README.txt`, strToU8(readme)]])
  addReadableTree(tree, root, pages, editor, files)
  if (trash.length) addReadableTree(trash.map((entry) => entry.node), `${root}Trash/`, pages, editor, files)

  onProgress({ step: 'Packing the file', current: 0, total: 0 })
  const zippable: Zippable = {}
  for (const [path, bytes] of files) zippable[path] = bytes
  const saved = await getPlatformBridge().exportZip(zipSync(zippable, { level: 6 }), `${name}.zip`)
  return saved ? { path: saved.path, unreadable } : null
}

/** Builds the archive and asks the user where to save it. Null when canceled. */
export async function exportCorpusToFile(
  editor: any,
  options: SeiExportOptions,
  onProgress?: ProgressCallback,
): Promise<{ path: string; skippedSnapshots: number } | null> {
  const result = await buildCorpusArchive(editor, options, onProgress)
  onProgress?.({ step: 'Saving', current: 0, total: 0 })
  const saved = await getPlatformBridge().saveSeiFile(result.bytes, result.fileName)
  return saved ? { path: saved.path, skippedSnapshots: result.skippedSnapshots } : null
}
